import React, { useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { format } from "date-fns";
import { Icon } from "@iconify/react";
import Dialog from "@mui/material/Dialog";
import _ from "lodash";
import { useGetMilestoneViewDetails } from "../../queries/milestone";
import { useExportToExcel } from "../../queries/esopPlan";
import { MilestoneViewDetail, VestState } from "../../types/milestone";
import {
  Box,
  ButtonPrimary1,
  ExportTableData,
  HStack,
  TableDataExportProps,
  VStack,
} from "../../components/utils";
import BCHeader from "../../shared/BCHeader";
import StatusLabel from "../esopOverview/StatusLabel";
import { ExportImport } from "../esopOverview/GrantsTable";
import { sortComparator } from "../../utils/agGridUtils";
import { formatDisplayDate } from "../../utils/date";

const milestoneViewHeadersObj: Record<string, string> = {
  grantIdentifier: "Grant ID",
  employeeName: "Employee Name",
  employeeId: "Employee ID",
  optionsGranted: "Milestone Options",
  vestingDate: "Vesting Date",
  vestingState: "Vesting State",
  completionDate: "Completion Date",
};
const milestoneViewHeadersList = Object.keys(milestoneViewHeadersObj);

function formatDateSafe(value: any): string {
  if (!value) return "-";
  try {
    return formatDisplayDate(new Date(value));
  } catch {
    return "-";
  }
}

export function ViewMilestone() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: response, isLoading } = useGetMilestoneViewDetails(id ?? "");
  const milestoneView = response?.data;

  const [searchText, setSearchText] = useState("");
  const [exportDialog, setExportDialog] = useState<TableDataExportProps>({
    open: false,
  });
  const gridApi = useRef<AgGridReact<MilestoneViewDetail>>(null);
  const [filteredRows, setFilteredRows] = useState<MilestoneViewDetail[]>([]);

  const updateFilteredRows = () => {
    if (!gridApi.current?.api) return;
    const rows: MilestoneViewDetail[] = [];
    gridApi.current.api.forEachNodeAfterFilter((node) => {
      if (node.data) rows.push(node.data);
    });
    setFilteredRows(rows);
  };

  const [groupBy, setGroupBy] = useState<
    "none" | "grantIdentifier" | "employeeName"
  >("none");

  const groupByOptions: {
    value: "none" | "grantIdentifier" | "employeeName";
    label: string;
  }[] = [
    { value: "none", label: "None" },
    { value: "grantIdentifier", label: "Grant ID" },
    { value: "employeeName", label: "Employee Name" },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      columnsMenuParams: { suppressColumnFilter: true },
      comparator: sortComparator,
      filterParams: { buttons: ["reset"], maxNumConditions: 5 },
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  const colDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "GRANT ID",
        field: "grantIdentifier",
        sortable: true,
        cellStyle: { "line-height": "20px", "padding-top": "15px" },
        comparator: (a, b) => (a === b ? 0 : a > b ? 1 : -1),
        width: 200,
        menuTabs: ["filterMenuTab"],
        autoHeight: true,
        wrapText: true,
        filter: true,
        rowGroup: groupBy === "grantIdentifier",
        hide: groupBy === "grantIdentifier",
      },
      {
        headerName: "EMPLOYEE NAME",
        field: "employeeName",
        sortable: true,
        cellStyle: { "line-height": "20px", "padding-top": "15px" },
        comparator: (a, b) => (a === b ? 0 : a > b ? 1 : -1),
        width: 200,
        menuTabs: ["filterMenuTab"],
        autoHeight: true,
        wrapText: true,
        filter: true,
        rowGroup: groupBy === "employeeName",
        hide: groupBy === "employeeName",
      },
      {
        headerName: "EMPLOYEE ID",
        field: "employeeId",
        sortable: true,
        cellStyle: { "line-height": "20px", "padding-top": "15px" },
        comparator: (a, b) => (a === b ? 0 : a > b ? 1 : -1),
        width: 160,
        menuTabs: ["filterMenuTab"],
        autoHeight: true,
        wrapText: true,
        filter: true,
      },
      {
        headerName: "OPTIONS",
        field: "optionsGranted",
        sortable: true,
        cellStyle: { "line-height": "20px", "padding-top": "15px" },
        comparator: (a, b) => (a === b ? 0 : a > b ? 1 : -1),
        width: 180,
        menuTabs: ["filterMenuTab"],
        autoHeight: true,
        wrapText: true,
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
        suppressAggFuncInHeader: true,
        valueFormatter: (params) =>
          params.value != null
            ? parseFloat(params.value.toFixed(4)).toString()
            : "-",
      },
      {
        headerName: "VESTING DATE",
        field: "vestingDate",
        sortable: true,
        comparator: (a, b) => (a === b ? 0 : a > b ? 1 : -1),
        width: 160,
        menuTabs: ["filterMenuTab"],
        autoHeight: true,
        wrapText: true,
        filter: "agDateColumnFilter",
        valueFormatter: (params) => formatDateSafe(params.value),
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (dateFromFilter.getTime() === new Date(cellValue).getTime())
              return 0;
            return dateFromFilter.getTime() > new Date(cellValue).getTime()
              ? -1
              : 1;
          },
        },
      },
      {
        headerName: "VESTING STATE",
        field: "vestingState",
        sortable: true,
        cellRenderer: ({ value }: { value: string }) => (
          <StatusLabel state={value} _className="text-xs" />
        ),
        filterValueGetter: (params) => params.data?.vestingState,
        comparator: (a, b) =>
          (a || "").toString().localeCompare((b || "").toString()),
        width: 160,
        menuTabs: ["filterMenuTab"],
        autoHeight: true,
        filter: true,
      },
      {
        headerName: "COMPLETION DATE",
        field: "completionDate",
        sortable: true,
        comparator: (a, b) => (a === b ? 0 : a > b ? 1 : -1),
        width: 160,
        menuTabs: ["filterMenuTab"],
        autoHeight: true,
        wrapText: true,
        filter: "agDateColumnFilter",
        valueFormatter: (params) => formatDateSafe(params.value),
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (!cellValue) return -1;
            if (dateFromFilter.getTime() === new Date(cellValue).getTime())
              return 0;
            return dateFromFilter.getTime() > new Date(cellValue).getTime()
              ? -1
              : 1;
          },
        },
      },
    ],
    [groupBy]
  );

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      wrapText: true,
      flex: 1,
      sortable: false,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: [],
      },
      menuTabs: [],
      minWidth: 150,
      filter: true,
      cellRendererParams: {
        suppressCount: false,
      },
    }),
    [groupBy]
  );
  const rowData = useMemo(() => {
    const rows = milestoneView?.rows ?? [];
    setFilteredRows(rows);
    return rows;
  }, [milestoneView]);

  const targetDateDisplay = milestoneView
    ? milestoneView.vestingOffset != null
      ? `+ ${milestoneView.vestingOffset} ${milestoneView.offsetType ?? ""}`
      : formatDateSafe(milestoneView.targetDate)
    : "-";

  return (
    <>
      <VStack className="gap-4 rounded-lg">
        <BCHeader
          className="items-baseline pb-4"
          bcTitle="Performance Milestones"
          bcSubTitle="View milestone"
        />
      </VStack>
      <Box
        className={`border border-borderColor drop-shadow-box bg-white ${
          isLoading ? "loading" : ""
        }`}
      >
        {/* Header bar */}
        <HStack className="items-center justify-between py-2 bg-white rounded-md">
          <VStack className="px-6 lg:justify-start">
            <p className="text-lg font-medium text-gray-600">
              {milestoneView?.milestoneName ?? ""}
            </p>
            <p className="mr-4 text-sm font-medium text-gray-500 md:mr-12">
              {`${
                rowData.length > 0
                  ? _.uniqBy(rowData, (r) => r.employeeId).length
                  : 0
              } employee(s) \u00B7 ${
                rowData.length > 0
                  ? _.uniqBy(rowData, (r) => r.grantId).length
                  : 0
              } grant(s)`}
            </p>
          </VStack>
          <HStack className="items-center justify-end gap-4 py-2 pr-4">
            <HStack className="items-center p-2 rounded min-w-72 bg-slate-light text-slate-dark">
              <Icon icon="fe:search" width="24" className="mr-2" />
              <input
                type="text"
                className="w-full text-xs font-normal border-0 outline-none bg-inherit"
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </HStack>
            <ExportImport
              actions={[{ name: "Export All To Excel" }]}
              onAction={(action) => {
                if (action.name === "Export All To Excel") {
                  if (rowData.length === 0) return;
                  const exportData = rowData.map((r) => ({
                    ...r,
                    vestingDate: r.vestingDate
                      ? formatDateSafe(r.vestingDate)
                      : "-",
                    completionDate: r.completionDate
                      ? formatDateSafe(r.completionDate)
                      : "-",
                  }));
                  setExportDialog({
                    open: true,
                    data: exportData,
                    headers: milestoneViewHeadersList.map((key) => ({
                      key,
                      value: milestoneViewHeadersObj[key],
                    })),
                    heading: milestoneView?.milestoneName ?? "Milestone",
                    fileName: `Milestone_${
                      milestoneView?.milestoneName ?? "view"
                    }`,
                    tableName: "Milestone",
                  });
                }
              }}
            />
          </HStack>
        </HStack>

        {/* Milestone meta strip */}
        {milestoneView && (
          <div className="py-4 px-2 border-t border-gray-100 bg-white">
            <HStack className="flex-wrap items-center border border-gray-200 rounded-lg bg-white px-4 py-3 gap-0">
              {/* STATUS */}
              <VStack className="gap-2 pr-6">
                <p className="text-xxs font-medium text-gray-light uppercase">
                  Status
                </p>
                <StatusLabel
                  state={milestoneView.milestoneStatus.toString()}
                  _className="text-xs "
                />
              </VStack>
              <div className="self-stretch w-px bg-gray-200 mx-2" />

              {/* TARGET DATE / OFFSET */}
              <VStack className="gap-2 px-6">
                <p className="text-xxs font-medium text-gray-light uppercase">
                  Target Date / Offset
                </p>
                <p className="text-xs font-semibold text-gray-dark">
                  {targetDateDisplay}
                </p>
              </VStack>
              <div className="self-stretch w-px bg-gray-200 mx-2" />

              {/* TYPE */}
              <VStack className="gap-2 px-6">
                <p className="text-xxs font-medium text-gray-light uppercase">
                  Type
                </p>
                <p className="text-xs font-semibold text-gray-dark">
                  {milestoneView.milestoneType}
                </p>
              </VStack>
              <div className="self-stretch w-px bg-gray-200 mx-2" />

              {/* CLOSURE */}
              <VStack className="gap-2 px-6">
                <p className="text-xxs font-medium text-gray-light uppercase">
                  Closure
                </p>
                <p className="text-xs font-semibold text-gray-dark">
                  {milestoneView.closureType}
                </p>
              </VStack>

              {milestoneView.metaData?.classification && (
                <>
                  <div className="self-stretch w-px bg-gray-200 mx-2" />
                  <VStack className="gap-2 px-6">
                    <p className="text-xxs font-medium text-gray-light uppercase">
                      Classification
                    </p>
                    <p className="text-xs font-semibold text-gray-dark">
                      {milestoneView.metaData.classification
                        .charAt(0)
                        .toUpperCase() +
                        milestoneView.metaData.classification
                          .slice(1)
                          .toLowerCase()}
                    </p>
                  </VStack>
                </>
              )}

              {milestoneView.completionDate && (
                <>
                  <div className="self-stretch w-px bg-gray-200 mx-2" />
                  <VStack className="gap-1 px-6">
                    <p className="text-xxs font-medium text-gray-light uppercase">
                      Completed On
                    </p>
                    <p className="text-xs font-semibold text-gray-dark">
                      {formatDateSafe(milestoneView.completionDate)}
                    </p>
                  </VStack>
                </>
              )}

              {/* State count pills */}
              <HStack className="items-center gap-3 ml-auto">
                {(
                  [
                    { label: "Vested", state: VestState.VESTED },
                    { label: "Accelerated", state: VestState.ACCELERATED },
                    { label: "Lapsed", state: VestState.LAPSED },
                    { label: "Active", state: VestState.ACTIVE },
                  ] as const
                ).map(({ label, state }) => {
                  const count = filteredRows
                    .filter((r) => r.vestingState === state)
                    .reduce((sum, r) => sum + (r.optionsGranted ?? 0), 0)
                    .toLocaleString();
                  return (
                    <HStack
                      key={label}
                      className="items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-xs font-medium"
                    >
                      <span className="text-xs font-semibold text-gray-dark">
                        {count}
                      </span>
                      <span className="text-gray-light">{label}</span>
                    </HStack>
                  );
                })}
              </HStack>
            </HStack>
          </div>
        )}

        {/* Group by control */}
        <div className="px-4 py-2 border-t border-gray-100 bg-white">
          <HStack className="items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Group by:</span>
            {groupByOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setGroupBy(opt.value)}
                className={`text-xs px-3 py-1 rounded border transition-colors ${
                  groupBy === opt.value
                    ? "bg-orange-501 text-white border-orange-501"
                    : "bg-white text-gray-600 border-gray-300 hover:border-orange-501 hover:text-orange-501"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </HStack>
        </div>

        {/* Grid */}
        <Box
          style={{ height: "calc(84vh - 100px)" }}
          className="w-full h-full max-h-full overflow-x-auto ag-theme-material"
        >
          <AgGridReact
            ref={gridApi}
            rowData={rowData}
            quickFilterText={searchText}
            onGridReady={updateFilteredRows}
            onFilterChanged={updateFilteredRows}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            suppressAggFuncInHeader
            groupDisplayType="singleColumn"
            groupDefaultExpanded={0}
            alwaysShowHorizontalScroll
            alwaysMultiSort
            animateRows
            suppressRowTransform
            suppressCopyRowsToClipboard
            suppressCopySingleCellRanges
            suppressCellFocus
            suppressMenuHide
            tooltipShowDelay={1000}
            rowClass="border-t border-dashed"
            overlayNoRowsTemplate='<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
            getRowStyle={() => ({ background: "#ffffff" })}
          />
        </Box>
        <HStack className="justify-end p-2 bg-white">
          <ButtonPrimary1 onClick={() => navigate(-1)}>Back</ButtonPrimary1>
        </HStack>
      </Box>
      <Dialog open={exportDialog.open ?? false} maxWidth="md">
        <ExportTableData
          onDiscard={() => setExportDialog({ open: false })}
          data={exportDialog.data ?? []}
          fileName={exportDialog.fileName ?? ""}
          headers={exportDialog.headers ?? []}
          heading={exportDialog.heading ?? ""}
          tableName={exportDialog.tableName ?? ""}
        />
      </Dialog>
    </>
  );
}
